"use client";
import { FC } from "react";
import FallbackImage from "@/app/components/elements/FallbackImage";
import Link from "next/link";
import { trackProductClick } from "@/app/lib/tracking/interactions";

interface CartProductCardProps {
  id: number;
  image: string;
  name: string;
  quantity: number;
  price: string;
  variant?: {
    color?: string;
    size?: string;
  };
  className?: string;
  showPrice?: boolean;
  imageSize?: "sm" | "md" | "lg";
  slug: string;
}

const CartProductCard: FC<CartProductCardProps> = ({
  id,
  image,
  name,
  quantity,
  price,
  variant,
  className = "",
  showPrice = true,
  imageSize = "md",
  slug,
}) => {
  const imageSizes = {
    sm: { width: 48, height: 64, className: "w-12 h-16" },
    md: { width: 64, height: 80, className: "w-16 h-20" },
    lg: { width: 80, height: 96, className: "w-20 h-24" },
  };

  const textSizes = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  const sizeConfig = imageSizes[imageSize];

  return (
    <Link
      href={`/${slug}`}
      onClick={() => trackProductClick(id)}
      className={`flex gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors ${className}`}
    >
      <div
        className={`${sizeConfig.className} overflow-hidden rounded-lg bg-gray-100 shrink-0 relative`}
      >
        <FallbackImage
          src={image || ""}
          alt={name}
          fill
          className="object-cover"
          fallbackClassName="object-contain opacity-60"
          sizes={`${sizeConfig.width}px`}
          quality={70}
        />
      </div>
      <div className="flex-1 min-w-0">
        <h4
          className={`${textSizes[imageSize]} font-medium text-gray-900 line-clamp-2`}
        >
          {name}
        </h4>
        <div className="flex items-center gap-2 mt-1">
          {variant && (variant.color || variant.size) && (
            <p className="text-xs text-gray-500">
              {variant.color && variant.size
                ? `${variant.color} / ${variant.size}`
                : variant.color || variant.size}
            </p>
          )}
          <p className="text-xs text-gray-500">Adet: {quantity}</p>
        </div>
        {showPrice && (
          <p
            className={`${textSizes[imageSize]} font-semibold text-gray-900 mt-1`}
          >
            {price}
          </p>
        )}
      </div>
    </Link>
  );
};

export default CartProductCard;
